// @ts-nocheck
import { useState, useEffect, useMemo, useCallback } from 'react';
import _ from 'lodash';
import { isSlugBlacklisted } from '@staty/shared/blacklistedSlugs';

// Use the same API base as the rest of admin (vite.config injects `process.env`).
// The old `import.meta.env.VITE_API_BASE_URL` is unset in every env, so it fell
// back to localhost:1337 — breaking the slug check (and thus Continue) on prod.
const baseUrl = `${process.env.API_URL || 'http://localhost:1337'}/api/v1`;

export type SlugStatus = 'idle' | 'checking' | 'available' | 'taken';

/**
 * INT-0040: ONE availability check for the wizard and the Settings "Public
 * URL" field. The API applies every rule (format, blacklist, live + soft-
 * deleted + previous slugs). Network failure → 'idle' (never a false
 * "available"). `excludeCurrent` lets Settings treat the org's own slug as
 * "unchanged" rather than taken.
 */
export async function checkSlug(slug: string): Promise<{ status: SlugStatus; error?: string }> {
  if (!slug || slug.length < 3) return { status: 'idle' };
  if (isSlugBlacklisted(slug)) return { status: 'taken', error: 'This URL is not available.' };
  try {
    const res = await fetch(`${baseUrl}/organizations/slug-available?name=${encodeURIComponent(slug)}`);
    if (!res.ok) return { status: 'idle' };
    const { result } = await res.json();
    if (result && result.available) return { status: 'available' };
    return { status: 'taken', error: (result && result.error) || 'This URL is already taken.' };
  } catch {
    return { status: 'idle' };
  }
}

export function buildLeagueSlug(displayName: string): string {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

export function useSlugAvailability() {
  const [leagueName, setLeagueNameState] = useState('');
  const [customSlug, setCustomSlug] = useState('');
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [slugSuggestions, setSlugSuggestions] = useState<string[]>([]);

  const leagueUrl = buildLeagueSlug(leagueName);
  const finalSlug = customSlug || leagueUrl;

  const generateSuggestions = useCallback((slug: string, displayName: string): string[] => {
    const base = slug.replace(/-/g, '');
    const words = displayName.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim().split(/\s+/);
    const suggestions = [
      `the-${slug}`,
      base,
      words.length > 1 ? `${words[0]}-${words[words.length - 1]}` : `${slug}-league`,
    ];
    return suggestions.filter(s => s !== slug && s.length >= 3);
  }, []);

  const checkSlugAvailability = useMemo(
    () =>
      _.debounce(async (slug: string, displayName: string) => {
        if (!slug || slug.length < 3) {
          setSlugStatus('idle');
          setSlugSuggestions([]);
          return;
        }

        if (isSlugBlacklisted(slug)) {
          setSlugStatus('taken');
          setSlugSuggestions(generateSuggestions(slug, displayName));
          return;
        }

        setSlugStatus('checking');

        const { status } = await checkSlug(slug);
        setSlugStatus(status);
        setSlugSuggestions(status === 'taken' ? generateSuggestions(slug, displayName) : []);
      }, 500),
    [generateSuggestions]
  );

  useEffect(() => {
    const slugToCheck = customSlug || leagueUrl;
    if (slugToCheck) {
      checkSlugAvailability(slugToCheck, leagueName);
    } else {
      setSlugStatus('idle');
      setSlugSuggestions([]);
    }
    return () => checkSlugAvailability.cancel();
  }, [customSlug, leagueUrl, leagueName, checkSlugAvailability]);

  const setLeagueName = (value: string) => {
    setLeagueNameState(value);
    setCustomSlug('');
  };

  const handleSlugChange = (value: string) => {
    const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setCustomSlug(sanitized);
  };

  const selectSuggestion = (suggestion: string) => {
    setCustomSlug(suggestion);
  };

  return {
    leagueName,
    setLeagueName,
    customSlug,
    setCustomSlug,
    handleSlugChange,
    leagueUrl,
    finalSlug,
    slugStatus,
    slugSuggestions,
    selectSuggestion,
  };
}
